import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import api from '../utils/api';
import { useDashboardWebSocket } from '../hooks/useWebSocket';
import {
  TrendingUp,
  Activity,
  DollarSign,
  Percent,
  Plus,
  AlertTriangle,
  Power,
  LogOut,
  Link as LinkIcon,
  Info,
  BarChart3,
  Bell,
  PieChart,
  Wifi,
  Layers,
  Shield,
  X,
  History,
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  RefreshCw,
  Settings2,
  Check,
  Trash2,
} from 'lucide-react';
import { Input } from '../components/ui/input';

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [bots, setBots] = useState([]);
  const [stats, setStats] = useState({ total_pnl: 0, active_bots: 0, total_trades: 0, win_rate: 0, paper_capital: 10000, current_equity: 10000, total_allocated: 0, available_balance: 10000 });
  const [exchangeConnected, setExchangeConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedBot, setSelectedBot] = useState(null);
  const [botTrades, setBotTrades] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingTrades, setLoadingTrades] = useState(false);
  const [editingCapital, setEditingCapital] = useState(false);
  const [capitalInput, setCapitalInput] = useState('');
  const [savingCapital, setSavingCapital] = useState(false);

  // Check admin status
  // WebSocket handlers (will be used when WebSocket is available in production)
  const handleMarketUpdate = useCallback((data) => {
    setMarketData(data);
  }, []);

  const handleTradeExecuted = useCallback((trade) => {
    toast.success(`Trade executed: ${trade.side} ${trade.symbol} @ £${trade.price?.toFixed(2)}`);
    loadDashboard(); // Refresh data after trade
  }, []);

  const handleBotUpdate = useCallback((update) => {
    setBots(prev => prev.map(bot => 
      bot.id === update.id ? { ...bot, ...update } : bot
    ));
  }, []);

  const handleAlert = useCallback((alert) => {
    if (alert.severity === 'warning') {
      toast.warning(alert.message);
    } else if (alert.severity === 'error') {
      toast.error(alert.message);
    } else {
      toast.info(alert.message);
    }
  }, []);

  // Use WebSocket for real-time updates (graceful fallback to polling if not available)
  const { isConnected, connectionStatus } = useDashboardWebSocket({
    onMarketUpdate: handleMarketUpdate,
    onTradeExecuted: handleTradeExecuted,
    onBotUpdate: handleBotUpdate,
    onAlert: handleAlert,
  });

  const refreshStats = async () => {
    setRefreshing(true);
    try {
      const [botsRes, statsRes] = await Promise.allSettled([
        api.getBots(),
        api.getDashboardStats(),
      ]);
      if (botsRes.status === 'fulfilled') setBots(botsRes.value.data);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
    } catch (error) {
      toast.error('Failed to refresh');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    checkPaymentStatus();
    // Refresh dashboard periodically
    const interval = setInterval(() => {
      loadDashboard();
    }, 30000); // 30s refresh
    return () => clearInterval(interval);
  }, []);

  const checkPaymentStatus = async () => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = 2000;

      const poll = async () => {
        try {
          const response = await api.checkPaymentStatus(sessionId);
          if (response.data.payment_status === 'paid') {
            toast.success('Subscription activated successfully!');
            navigate('/dashboard', { replace: true });
            loadDashboard();
            return;
          }

          if (attempts < maxAttempts && response.data.status !== 'expired') {
            attempts++;
            setTimeout(poll, pollInterval);
          }
        } catch (error) {
          console.error('Payment status check error:', error);
        }
      };

      poll();
    }
  };

  const loadDashboard = async () => {
    try {
      // Fetch user first — if this fails (401), redirect to login
      let userRes;
      try {
        userRes = await api.getMe();
        setUser(userRes.data);
      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          localStorage.removeItem('apexion_token');
          navigate('/auth');
          return;
        }
        throw err;
      }

      // Fetch remaining data independently — don't let one failure break others
      const [botsRes, statsRes, exchangeRes, adminRes] = await Promise.allSettled([
        api.getBots(),
        api.getDashboardStats(),
        api.getExchangeStatus(),
        api.get('/admin/check'),
      ]);
      if (botsRes.status === 'fulfilled') setBots(botsRes.value.data);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (exchangeRes.status === 'fulfilled') setExchangeConnected(exchangeRes.value.data.connected);
      if (adminRes.status === 'fulfilled') setIsAdmin(adminRes.value.data.is_admin);
    } catch (error) {
      console.error('Dashboard load error:', error);
      toast.error('Failed to load dashboard — retrying...');
      // Retry once after 2 seconds for transient failures
      setTimeout(async () => {
        try {
          const userRes = await api.getMe();
          setUser(userRes.data);
          const [botsRes, statsRes, exchangeRes] = await Promise.allSettled([
            api.getBots(), api.getDashboardStats(), api.getExchangeStatus(),
          ]);
          if (botsRes.status === 'fulfilled') setBots(botsRes.value.data);
          if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
          if (exchangeRes.status === 'fulfilled') setExchangeConnected(exchangeRes.value.data.connected);
        } catch (retryErr) {
          if (retryErr.response?.status === 401 || retryErr.response?.status === 403) {
            localStorage.removeItem('apexion_token');
            navigate('/auth');
          }
        }
      }, 2000);
    } finally {
      setLoading(false);
    }
  };

  const loadBotTrades = async (bot) => {
    setSelectedBot(bot);
    setLoadingTrades(true);
    try {
      const response = await api.get(`/bots/${bot.id}/trades`);
      setBotTrades(response.data.trades || []);
    } catch (error) {
      console.error('Failed to load trades:', error);
      setBotTrades([]);
    } finally {
      setLoadingTrades(false);
    }
  };

  const closeBotTrades = () => {
    setSelectedBot(null);
    setBotTrades([]);
  };

  const handleSubscribe = async (tier) => {
    try {
      const response = await api.createCheckout(tier);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process subscription');
    }
  };

  const handleKillSwitch = async () => {
    if (window.confirm('Stop all active bots immediately?')) {
      try {
        await api.killSwitch();
        toast.success('All bots stopped');
        loadDashboard();
      } catch (error) {
        toast.error('Kill switch failed');
      }
    }
  };

  const handleDeleteBot = async (bot) => {
    if (!window.confirm(`Delete "${bot.strategy.replace('_', ' ')}" bot?\n\nThis will permanently remove the bot and all its trade history. This cannot be undone.`)) return;
    try {
      await api.delete(`/bots/${bot.id}`);
      toast.success('Bot deleted');
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete bot');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('apexion_token');
    localStorage.removeItem('apexion_user');
    navigate('/');
  };

  const handleSetCapital = async () => {
    const amount = parseFloat(capitalInput);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!window.confirm(`Set paper trading capital to £${amount.toLocaleString()}? This will reset all bot P&L and trade history.`)) return;
    setSavingCapital(true);
    try {
      const response = await api.put('/paper-trading/account', { capital: amount });
      toast.success(response.data.message);
      setEditingCapital(false);
      setCapitalInput('');
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update capital');
    } finally {
      setSavingCapital(false);
    }
  };

  const handleResetAccount = async () => {
    if (!window.confirm('Reset paper trading? This will clear all P&L and trade history but keep your capital amount.')) return;
    try {
      await api.post('/paper-trading/reset');
      toast.success('Paper trading account reset');
      loadDashboard();
    } catch (error) {
      toast.error('Failed to reset account');
    }
  };

  const handleCompoundProfits = async () => {
    const pnl = stats.total_pnl || 0;
    if (pnl <= 0) {
      toast.error('No profits to compound — total P&L must be positive');
      return;
    }
    const newCap = (stats.paper_capital || 0) + pnl;
    if (!window.confirm(`Add £${pnl.toFixed(2)} profit to your capital?\n\nCapital: £${(stats.paper_capital || 0).toLocaleString()} → £${newCap.toLocaleString()}\n\nAll trade history will be preserved.`)) return;
    try {
      const res = await api.post('/paper-trading/compound-profits');
      toast.success(res.data.message);
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to compound profits');
    }
  };

  const handleClearTrades = async () => {
    if (!window.confirm('Before clearing trades, make sure to download a report from Reports & Analytics so your completed trades are not lost.\n\nAre you sure you want to clear all trades?')) return;
    try {
      const response = await api.post('/trades/clear');
      toast.success(response.data.message || 'Trades cleared');
      loadDashboard();
    } catch (error) {
      toast.error('Failed to clear trades');
    }
  };

  const toggleBotStatus = async (bot) => {
    try {
      if (bot.status === 'active') {
        await api.pauseBot(bot.id);
        toast.success('Bot paused');
      } else {
        await api.startBot(bot.id);
        toast.success('Bot started');
      }
      loadDashboard();
    } catch (error) {
      toast.error('Failed to update bot status');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0D12] flex items-center justify-center">
        <div className="text-[#2F6BFF] text-lg">Loading...</div>
      </div>
    );
  }

  const needsSubscription = user && user.subscription_tier === 'none';

  return (
    <div className="min-h-screen bg-[#0B0D12] text-[#F5F7FA]">
      {/* Header */}
      <header className="glass-effect border-b border-[#1E293B] sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="text-2xl font-bold font-['Outfit'] tracking-tight">APEXION</div>
          <div className="flex items-center gap-4">
            {/* Real-time connection indicator - only show when connected */}
            {isConnected && (
              <Badge
                data-testid="dashboard-ws-status-badge"
                className="bg-green-500/20 text-green-400 border-green-500/50 rounded-sm flex items-center gap-1"
              >
                <Wifi className="w-3 h-3" />
                Live
              </Badge>
            )}
            {exchangeConnected ? (
              <Badge
                data-testid="dashboard-exchange-status-badge"
                className="bg-green-500/20 text-green-400 border-green-500/50 rounded-sm"
              >
                Kraken Connected
              </Badge>
            ) : user ? (
              <Badge
                data-testid="dashboard-exchange-status-badge"
                className="bg-[#1E293B] text-[#94A3B8] rounded-sm"
              >
                Exchange Not Connected
              </Badge>
            ) : null}
            {user && (
              <Badge
                data-testid="dashboard-subscription-badge"
                className={`${
                  user.subscription_tier === 'elite'
                    ? 'bg-[#C9A24D] text-[#0B0D12]'
                    : user.subscription_tier === 'pro'
                    ? 'bg-[#2F6BFF]'
                    : 'bg-[#1E293B]'
                } rounded-sm`}
              >
                {user.subscription_tier === 'none' ? 'No Subscription' : user.subscription_tier.toUpperCase()}
              </Badge>
            )}
            {isAdmin && (
              <Button
                data-testid="dashboard-admin-btn"
                onClick={() => navigate('/admin')}
                variant="ghost"
                size="sm"
                className="text-purple-400 hover:text-purple-300"
                title="Admin Panel"
              >
                <Shield className="w-4 h-4" />
              </Button>
            )}
            <Button
              data-testid="dashboard-notifications-btn"
              onClick={() => navigate('/settings/notifications')}
              variant="ghost"
              size="sm"
              className="text-[#94A3B8] hover:text-[#F5F7FA]"
              title="Notification Settings"
            >
              <Bell className="w-4 h-4" />
            </Button>
            <Button
              data-testid="dashboard-logout-btn"
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="text-[#94A3B8] hover:text-[#F5F7FA]"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 py-8">
        {/* Exchange Connection Banner */}
        {!exchangeConnected && !needsSubscription && (
          <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="mb-8">
            <Alert className="bg-[#2F6BFF]/10 border-[#2F6BFF]/50">
              <LinkIcon className="h-5 w-5 text-[#2F6BFF]" />
              <AlertDescription className="flex items-center justify-between">
                <div>
                  <p className="text-[#F5F7FA] font-semibold mb-1">Connect your Kraken account to start trading</p>
                  <p className="text-sm text-[#94A3B8]">
                    Link your exchange to enable automated paper trading. No withdrawal access required.
                  </p>
                </div>
                <Button
                  data-testid="dashboard-connect-exchange-btn"
                  onClick={() => navigate('/exchange')}
                  className="bg-[#2F6BFF] hover:bg-[#2F6BFF]/90 rounded-sm ml-4"
                >
                  Connect API
                </Button>
              </AlertDescription>
            </Alert>
          </motion.div>
        )}

        {/* No Subscription Warning */}
        {needsSubscription && (
          <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
            <Card className="bg-[#EF4444]/10 border-[#EF4444]/50 mb-8">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <AlertTriangle className="w-6 h-6 text-[#EF4444] flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="font-semibold mb-2">Subscription Required</h3>
                    <p className="text-sm text-[#94A3B8] mb-4">
                      You need an active subscription to create and run trading bots.
                    </p>
                    <div className="flex gap-3">
                      <Button
                        data-testid="subscribe-core-btn"
                        onClick={() => handleSubscribe('core')}
                        className="bg-green-500 hover:bg-green-500/90 rounded-sm"
                      >
                        Core - £99/mo (1 Bot)
                      </Button>
                      <Button
                        data-testid="subscribe-pro-btn"
                        onClick={() => handleSubscribe('pro')}
                        className="bg-[#2F6BFF] hover:bg-[#2F6BFF]/90 rounded-sm"
                      >
                        Pro - £499/mo (3 Bots)
                      </Button>
                      <Button
                        data-testid="subscribe-elite-btn"
                        onClick={() => handleSubscribe('elite')}
                        className="bg-[#C9A24D] hover:bg-[#C9A24D]/90 text-[#0B0D12] rounded-sm"
                      >
                        Elite - £999/mo (6 Bots)
                      </Button>
                    </div>
                    <p className="text-xs text-[#94A3B8] mt-3">Paid plans: All purchases are final. No refunds.</p>
                    
                    {/* Claim Owner Button - only for first setup */}
                    <div className="mt-4 pt-4 border-t border-[#1E293B]">
                      <Button
                        data-testid="claim-owner-btn"
                        onClick={async () => {
                          try {
                            await api.post('/admin/claim-owner');
                            toast.success('You are now the platform owner!');
                            window.location.reload();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Could not claim owner access');
                          }
                        }}
                        variant="outline"
                        className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                      >
                        <Shield className="w-4 h-4 mr-2" /> Claim Owner Access (First Setup Only)
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Trading Account */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="mb-8"
          data-testid="paper-trading-account"
        >
          <Card className={`bg-gradient-to-br from-[#15171C] to-[#0B0D12] ${stats.has_live_bots ? 'border-red-500/50' : 'border-[#C9A24D]/30'}`}>
            <CardContent className="pt-6 pb-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${stats.has_live_bots ? 'bg-red-500/20' : 'bg-[#C9A24D]/20'}`}>
                    <Wallet className={`w-5 h-5 ${stats.has_live_bots ? 'text-red-400' : 'text-[#C9A24D]'}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold font-['Outfit']">
                      {stats.has_live_bots ? 'Live Trading Account' : 'Paper Trading Account'}
                    </h3>
                    <p className="text-xs text-[#94A3B8]">
                      {stats.has_live_bots ? 'Connected exchange balance' : 'Simulated USD balance'}
                    </p>
                  </div>
                  {stats.has_live_bots && (
                    <Badge className="bg-red-500 text-white text-xs animate-pulse">LIVE</Badge>
                  )}
                </div>
                {!stats.has_live_bots && (
                  <div className="flex items-center gap-2">
                    <Button
                      data-testid="edit-capital-btn"
                      variant="outline"
                      size="sm"
                      onClick={() => { setEditingCapital(!editingCapital); setCapitalInput(String(stats.paper_capital || 10000)); }}
                      className="border-[#C9A24D]/50 text-[#C9A24D] hover:bg-[#C9A24D]/10"
                    >
                      <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Set Capital
                    </Button>
                    {(stats.total_pnl || 0) > 0 && (
                      <Button
                        data-testid="compound-profits-btn"
                        variant="outline"
                        size="sm"
                        onClick={handleCompoundProfits}
                        className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                      >
                        <TrendingUp className="w-3.5 h-3.5 mr-1.5" /> Compound Profits
                      </Button>
                    )}
                    <Button
                      data-testid="reset-account-btn"
                      variant="outline"
                      size="sm"
                      onClick={handleResetAccount}
                      className="border-[#1E293B] text-[#94A3B8] hover:bg-[#1E293B]"
                    >
                      <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reset
                    </Button>
                  </div>
                )}
              </div>

              {editingCapital && (
                <div className="mb-5 p-4 rounded-lg bg-[#C9A24D]/10 border border-[#C9A24D]/30">
                  <p className="text-sm text-[#94A3B8] mb-2">Enter your paper trading capital (USD):</p>
                  <div className="flex gap-3">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] font-semibold">£</span>
                      <Input
                        data-testid="capital-input"
                        type="number"
                        min="100"
                        max="10000000"
                        step="100"
                        value={capitalInput}
                        onChange={(e) => setCapitalInput(e.target.value)}
                        placeholder="10000"
                        className="pl-7 bg-[#0B0D12] border-[#1E293B] font-['JetBrains_Mono']"
                      />
                    </div>
                    <Button
                      data-testid="save-capital-btn"
                      onClick={handleSetCapital}
                      disabled={savingCapital}
                      className="bg-[#C9A24D] hover:bg-[#C9A24D]/90 text-black font-semibold px-6"
                    >
                      {savingCapital ? 'Saving...' : <><Check className="w-4 h-4 mr-1" /> Apply</>}
                    </Button>
                  </div>
                  <p className="text-xs text-[#94A3B8] mt-2">This will reset all bot P&L and trade history.</p>
                  <div className="flex gap-2 mt-3">
                    {[1000, 5000, 10000, 25000, 50000, 100000].map(preset => (
                      <button
                        key={preset}
                        onClick={() => setCapitalInput(String(preset))}
                        className={`text-xs px-2.5 py-1 rounded border transition-all ${
                          capitalInput === String(preset)
                            ? 'border-[#C9A24D] bg-[#C9A24D]/20 text-[#C9A24D]'
                            : 'border-[#1E293B] text-[#94A3B8] hover:border-[#C9A24D]/50'
                        }`}
                      >
                        £{preset.toLocaleString()}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 rounded-lg bg-[#0B0D12]/80 border border-[#1E293B]">
                  <p className="text-xs text-[#94A3B8] mb-1">{stats.has_live_bots ? 'Exchange Balance' : 'Starting Capital'}</p>
                  <p className="text-lg font-bold font-['JetBrains_Mono'] text-[#F5F7FA]" data-testid="starting-capital">
                    £{(stats.has_live_bots && stats.exchange_balance != null ? stats.exchange_balance : stats.paper_capital || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-[#0B0D12]/80 border border-[#1E293B]">
                  <p className="text-xs text-[#94A3B8] mb-1">Allocated to Bots</p>
                  <p className="text-lg font-bold font-['JetBrains_Mono'] text-[#2F6BFF]" data-testid="allocated-amount">
                    £{(stats.total_allocated || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-[#0B0D12]/80 border border-[#1E293B]">
                  <p className="text-xs text-[#94A3B8] mb-1">Available USD</p>
                  <p className="text-lg font-bold font-['JetBrains_Mono'] text-[#C9A24D]" data-testid="available-balance">
                    £{(stats.available_balance || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-[#0B0D12]/80 border border-[#1E293B]">
                  <p className="text-xs text-[#94A3B8] mb-1">Current Equity</p>
                  <p className={`text-lg font-bold font-['JetBrains_Mono'] ${(stats.current_equity || 0) >= (stats.paper_capital || 0) ? 'text-green-400' : 'text-red-400'}`} data-testid="current-equity">
                    £{(stats.current_equity || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Refresh + Stats Grid */}
        <div className="flex items-center justify-end mb-3">
          <button
            data-testid="refresh-stats-btn"
            onClick={refreshStats}
            disabled={refreshing}
            className="flex items-center gap-1.5 text-xs text-[#94A3B8] hover:text-[#2F6BFF] transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {[
            {
              icon: <DollarSign className="w-5 h-5" />,
              label: 'Total P&L',
              value: `£${stats.total_pnl.toFixed(2)}`,
              color: stats.total_pnl >= 0 ? 'text-green-500' : 'text-red-500',
            },
            { icon: <Activity className="w-5 h-5" />, label: 'Active Bots', value: stats.active_bots },
            { icon: <TrendingUp className="w-5 h-5" />, label: 'Total Trades', value: stats.total_trades, showClear: true },
            { icon: <Percent className="w-5 h-5" />, label: 'Win Rate', value: `${stats.win_rate.toFixed(1)}%` },
          ].map((stat, idx) => (
            <motion.div
              key={idx}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: idx * 0.1 }}
            >
              <Card className="bg-[#15171C] border-[#1E293B]">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-[#94A3B8]">{stat.label}</span>
                    <div className="text-[#2F6BFF]">{stat.icon}</div>
                  </div>
                  <div className={`text-2xl font-bold font-['JetBrains_Mono'] ${stat.color || ''}`}>{stat.value}</div>
                  {stat.showClear && stats.total_trades > 0 && (
                    <button
                      data-testid="clear-trades-btn"
                      onClick={handleClearTrades}
                      className="mt-2 text-xs text-red-400 hover:text-red-300 flex items-center gap-1 transition-colors"
                    >
                      <Trash2 className="w-3 h-3" /> Clear Trades
                    </button>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Quick Actions Panel */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <h3 className="text-lg font-semibold font-['Outfit'] mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Strategy Performance */}
            <Card 
              className="bg-[#15171C] border-[#1E293B] hover:border-[#2F6BFF]/50 transition-all cursor-pointer group"
              onClick={() => navigate('/performance')}
              data-testid="quick-action-performance"
            >
              <CardContent className="pt-6 pb-6 text-center">
                <div className="w-12 h-12 rounded-full bg-[#2F6BFF]/20 flex items-center justify-center mx-auto mb-3 group-hover:bg-[#2F6BFF]/30 transition-all">
                  <TrendingUp className="w-6 h-6 text-[#2F6BFF]" />
                </div>
                <p className="font-medium text-sm">Strategy Performance</p>
                <p className="text-xs text-[#94A3B8] mt-1">View detailed analytics</p>
              </CardContent>
            </Card>

            {/* Reports */}
            <Card 
              className="bg-[#15171C] border-[#1E293B] hover:border-green-500/50 transition-all cursor-pointer group"
              onClick={() => navigate('/reports')}
              data-testid="quick-action-reports"
            >
              <CardContent className="pt-6 pb-6 text-center">
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-3 group-hover:bg-green-500/30 transition-all">
                  <BarChart3 className="w-6 h-6 text-green-400" />
                </div>
                <p className="font-medium text-sm">Reports</p>
                <p className="text-xs text-[#94A3B8] mt-1">Download PDF reports</p>
              </CardContent>
            </Card>

            {/* Manage Keys */}
            <Card 
              className="bg-[#15171C] border-[#1E293B] hover:border-purple-500/50 transition-all cursor-pointer group"
              onClick={() => navigate('/api-keys')}
              data-testid="quick-action-keys"
            >
              <CardContent className="pt-6 pb-6 text-center">
                <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-3 group-hover:bg-purple-500/30 transition-all">
                  <LinkIcon className="w-6 h-6 text-purple-400" />
                </div>
                <p className="font-medium text-sm">Manage Keys</p>
                <p className="text-xs text-[#94A3B8] mt-1">Exchange API keys</p>
              </CardContent>
            </Card>

            {/* Profit Simulator */}
            <Card 
              className="bg-[#15171C] border-[#1E293B] hover:border-[#C9A24D]/50 transition-all cursor-pointer group"
              onClick={() => navigate('/simulator')}
              data-testid="quick-action-simulator"
            >
              <CardContent className="pt-6 pb-6 text-center">
                <div className="w-12 h-12 rounded-full bg-[#C9A24D]/20 flex items-center justify-center mx-auto mb-3 group-hover:bg-[#C9A24D]/30 transition-all">
                  <PieChart className="w-6 h-6 text-[#C9A24D]" />
                </div>
                <p className="font-medium text-sm">Profit Simulator</p>
                <p className="text-xs text-[#94A3B8] mt-1">Test your capital</p>
              </CardContent>
            </Card>
          </div>
        </motion.div>

        {/* Bots Section */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold font-['Outfit']">Your Bots</h2>
          <div className="flex gap-3">
            <Button
              data-testid="dashboard-create-bot-btn"
              onClick={() => {
                if (!exchangeConnected) {
                  toast.error('Please connect your exchange first');
                  navigate('/api-keys');
                } else {
                  navigate('/strategies');
                }
              }}
              className="bg-[#2F6BFF] hover:bg-[#2F6BFF]/90 rounded-sm"
              disabled={needsSubscription}
            >
              <Plus className="w-4 h-4 mr-2" /> Create Bot
            </Button>
            <Button
              data-testid="dashboard-killswitch-btn"
              onClick={handleKillSwitch}
              variant="destructive"
              className="rounded-sm"
              disabled={stats.active_bots === 0}
            >
              <Power className="w-4 h-4 mr-2" /> Kill Switch
            </Button>
          </div>
        </div>

        {bots.length === 0 ? (
          <Card className="bg-[#15171C] border-[#1E293B]">
            <CardContent className="pt-6 text-center py-12">
              <p className="text-[#94A3B8] mb-4">No bots created yet</p>
              {!exchangeConnected && !needsSubscription && (
                <div className="mb-4">
                  <Alert className="bg-[#2F6BFF]/10 border-[#2F6BFF]/50 inline-block max-w-md">
                    <Info className="h-4 w-4 text-[#2F6BFF]" />
                    <AlertDescription className="text-sm text-[#94A3B8]">
                      Connect your Kraken account first to start creating bots
                    </AlertDescription>
                  </Alert>
                </div>
              )}
              <Button
                data-testid="dashboard-empty-create-bot-btn"
                onClick={() => {
                  if (!exchangeConnected) {
                    navigate('/exchange');
                  } else {
                    navigate('/strategies');
                  }
                }}
                className="bg-[#2F6BFF] hover:bg-[#2F6BFF]/90 rounded-sm"
                disabled={needsSubscription}
              >
                {!exchangeConnected ? 'Connect Exchange' : 'Create Your First Bot'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bots.map((bot) => (
              <motion.div key={bot.id} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
                <Card className="bg-[#15171C] border-[#1E293B] hover:border-[#2F6BFF]/50 transition-colors">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-['Outfit'] capitalize">{bot.strategy.replace('_', ' ')}</CardTitle>
                      <Badge
                        data-testid={`bot-status-${bot.id}`}
                        className={`${
                          bot.status === 'active'
                            ? 'bg-green-500'
                            : bot.status === 'paused'
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                        } rounded-sm`}
                      >
                        {bot.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-[#94A3B8]">Mode</p>
                          <Badge className={`text-xs ${bot.trading_mode === 'live' ? 'bg-red-500' : 'bg-[#2F6BFF]'}`}>
                            {bot.trading_mode === 'live' ? 'LIVE' : 'PAPER'}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-xs text-[#94A3B8]">Allocation</p>
                          <p className="text-sm font-semibold font-['JetBrains_Mono']">
                            {bot.allocation_percentage || 10}% <span className="text-xs text-[#94A3B8]">(£{(bot.paper_balance || 0).toLocaleString(undefined, {maximumFractionDigits: 0})})</span>
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-[#94A3B8]">Trades</p>
                          <p className="text-sm font-semibold">{bot.total_trades || 0}</p>
                        </div>
                        <div>
                          <p className="text-xs text-[#94A3B8]">P&L</p>
                          <p
                            className={`text-sm font-bold font-['JetBrains_Mono'] ${
                              bot.paper_pnl >= 0 ? 'text-green-500' : 'text-red-500'
                            }`}
                          >
                            £{bot.paper_pnl.toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-[#94A3B8]">Win Rate</p>
                          <p className="text-sm font-semibold">{bot.win_rate.toFixed(1)}%</p>
                        </div>
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Button
                          data-testid={`bot-trades-${bot.id}`}
                          onClick={() => loadBotTrades(bot)}
                          size="sm"
                          variant="outline"
                          className="flex-1 border-[#1E293B] rounded-sm"
                        >
                          <History className="w-3 h-3 mr-1" /> Trades
                        </Button>
                        <Button
                          data-testid={`bot-toggle-${bot.id}`}
                          onClick={() => toggleBotStatus(bot)}
                          size="sm"
                          className={`flex-1 rounded-sm ${bot.status === 'active' ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'}`}
                        >
                          {bot.status === 'active' ? 'Pause' : 'Start'}
                        </Button>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          data-testid={`bot-view-${bot.id}`}
                          onClick={() => navigate(`/bot/${bot.id}`)}
                          size="sm"
                          variant="outline"
                          className="flex-1 border-[#2F6BFF]/50 text-[#2F6BFF] hover:bg-[#2F6BFF]/10 rounded-sm"
                        >
                          <Settings2 className="w-3 h-3 mr-1" /> Settings & Mode
                        </Button>
                        <Button
                          data-testid={`bot-restart-${bot.id}`}
                          onClick={async () => {
                            if (!window.confirm('Restart this bot? This will reset its P&L and trade count so it can trade again.')) return;
                            try {
                              await api.post(`/bots/${bot.id}/restart`);
                              toast.success('Bot restarted — P&L reset');
                              loadDashboard();
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Failed to restart bot');
                            }
                          }}
                          size="sm"
                          variant="outline"
                          className="border-[#C9A24D]/50 text-[#C9A24D] hover:bg-[#C9A24D]/10 rounded-sm"
                          disabled={bot.status === 'active'}
                        >
                          <RefreshCw className="w-3 h-3 mr-1" /> Restart
                        </Button>
                      </div>
                      <Button
                        data-testid={`bot-delete-${bot.id}`}
                        onClick={() => handleDeleteBot(bot)}
                        size="sm"
                        variant="ghost"
                        className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-sm"
                      >
                        <Trash2 className="w-3 h-3 mr-1" /> Delete Bot
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Legal Disclaimer */}
        <div className="mt-12 p-4 bg-[#15171C] border border-[#1E293B] rounded-sm">
          <p className="text-xs text-[#94A3B8] text-center">
            Trading involves risk. Past performance does not guarantee future results. You are in full control.
          </p>
        </div>
      </div>

      {/* Bot Trades Modal */}
      <AnimatePresence>
        {selectedBot && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={closeBotTrades}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#15171C] border border-[#1E293B] rounded-lg w-full max-w-2xl max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-[#1E293B]">
                <div>
                  <h2 className="text-xl font-semibold font-['Outfit'] capitalize">
                    {selectedBot.strategy.replace('_', ' ')} Trades
                  </h2>
                  <p className="text-sm text-[#94A3B8]">
                    Total: {selectedBot.total_trades || 0} trades | P&L: 
                    <span className={selectedBot.paper_pnl >= 0 ? ' text-green-400' : ' text-red-400'}>
                      {' '}£{selectedBot.paper_pnl?.toFixed(2) || '0.00'}
                    </span>
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeBotTrades}
                  className="text-[#94A3B8] hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Modal Content */}
              <div className="p-4 overflow-y-auto max-h-[60vh]">
                {loadingTrades ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2F6BFF]"></div>
                  </div>
                ) : botTrades.length === 0 ? (
                  <div className="text-center py-8">
                    <History className="w-12 h-12 text-[#94A3B8] mx-auto mb-3" />
                    <p className="text-[#94A3B8]">No trades executed yet</p>
                    <p className="text-xs text-[#64748B] mt-1">Trades will appear here when the bot executes them</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {botTrades.map((trade, index) => (
                      <div
                        key={trade.id || index}
                        className="bg-[#0B0D12] rounded-lg p-4 border border-[#1E293B]"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {trade.side === 'buy' ? (
                              <ArrowUpRight className="w-5 h-5 text-green-400" />
                            ) : (
                              <ArrowDownRight className="w-5 h-5 text-red-400" />
                            )}
                            <span className={`font-semibold uppercase ${trade.side === 'buy' ? 'text-green-400' : 'text-red-400'}`}>
                              {trade.side}
                            </span>
                            <span className="text-[#F5F7FA] font-medium">{trade.symbol}</span>
                          </div>
                          <span className={`font-bold font-['JetBrains_Mono'] ${(trade.pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {(trade.pnl || 0) >= 0 ? '+' : ''}£{(trade.pnl || 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-xs text-[#94A3B8]">Price</p>
                            <p className="font-['JetBrains_Mono']">£{trade.price?.toFixed(2) || '0.00'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-[#94A3B8]">Quantity</p>
                            <p className="font-['JetBrains_Mono']">{trade.quantity?.toFixed(6) || '0.00'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-[#94A3B8]">Time</p>
                            <p className="text-xs">{new Date(trade.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                        {trade.reason && (
                          <p className="text-xs text-[#64748B] mt-2 italic">{trade.reason}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Dashboard;